
import React, { useEffect, useState, useRef } from 'react';
import {
  FaRedo, FaUser, FaInfoCircle, FaSearch,
  FaBuilding, FaGlobe, FaChevronDown, FaSpinner
} from 'react-icons/fa';

// Google Apps Script URL
const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxBWwcEIqe5UECj1UkYk34pucTKXNSQEAdMZHVP1mB3t7MASeN9kJ-2MdlmlzPwSyHK/exec';

const categories = ['Shelf Corporation'];

const categoryIcons = {
  'Shelf Corporation': <FaBuilding />,
  // 'Website Purchase': <FaGlobe />,
};

function App() {
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);

  /* Close dropdown on outside click */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleCategorySelect = (cat) => {
    setCategory(cat);
    setDropdownOpen(false);
    setError('');
    setResult(null);
    setHasSearched(false);
  };

  const handleSearch = async () => {
    let cleanPhone = search.replace(/\D/g, '');
    if (!cleanPhone) {
      setError('Please enter a phone number');
      return;
    }
    if (cleanPhone.length < 10) {
      setError('Phone number should be at least 10 digits');
      return;
    }
    if (!category) {
      setError('Please select a category');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await fetch(`${SCRIPT_URL}?action=search&category=${encodeURIComponent(category)}&phone=${encodeURIComponent(cleanPhone)}`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server responded with status ${response.status}: ${errorText}`);
      }
      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else if (!data.data) {
        setError(`❌ User not found in ${category}.`);
      } else {
        setResult(data.data);
      }
    } catch (err) {
      setError(`Failed to search: ${err.message}`);
    } finally {
      setLoading(false);
      setHasSearched(true);
    }
  };

  const handleReset = () => {
    setCategory('');
    setSearch('');
    setResult('');
    setError('');
    setHasSearched(false);
  };

  /* Alert Box with heading */
  const AlertBox = ({ message }) => (
    <div className="mt-5">
      <h3 className="text-lg font-bold text-yellow-500 mb-2 flex items-center justify-start">
        <FaBuilding className="mr-2" /> Shelf Corporation Status
      </h3>
      <div className="flex items-start gap-2.5 p-3.5 bg-white text-blue-600 border-2 border-blue-600 rounded-lg">
        <FaInfoCircle className="text-xl" />
        <strong>{message}</strong>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      {/* Logo */}
      <div className="bg-gray-800">
        <img
          src="https://msgsndr-private.storage.googleapis.com/companyPhotos/dee76044-7b84-4f98-a487-4c1da2c2cc0e.png"
          alt="Logo"
          className="mx-auto h-28 w-auto object-contain"
        />
      </div>

      {/* Header */}
      <header
        className="bg-cover bg-center bg-fixed"
        style={{ backgroundImage: "url('https://storage.googleapis.com/msgsndr/OGQsmhvhp6xX235TWMKN/media/685350ccfdd93592d7981f0f.svg')" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <h1 className="text-4xl md:text-5xl sm:text-2xl font-bold leading-tight mt-6 text-yellow-500 banner-title">
            Get Instant Updates About Your Shelf Corporation Process
          </h1>
          <p className="text-lg md:text-xl text-black mt-4 font-bold banner-subtitle">
            Enter a mobile number to instantly retrieve Your shelf corporation info.
          </p>
        </div>

        {/* Category & Search */}
        <div className="search-section mb-6">
          <div className="relative text-left w-[90%] sm:w-[60%] md:w-[40%] lg:w-[30%] mx-auto">
            <button
              style={{ width: "100%" }}
              className="flex items-center justify-between px-4 py-4 rounded cursor-pointer bg-white text-black border border-yellow-500"
              ref={toggleRef}
              onClick={() => setDropdownOpen(prev => !prev)}
            >
              <span>{category || 'Select a category'}</span>
              <FaChevronDown className={`ml-2 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} style={{ color: '#dcae4c' }} />
            </button>
            <div
              ref={dropdownRef}
              className={`w-full mx-auto bg-white rounded shadow-lg transition-all duration-200 ${dropdownOpen ? 'block' : 'hidden'}`}
            >
              {categories.map(cat => (
                <div
                  key={cat}
                  className="px-4 py-3.5 hover:bg-gray-100 cursor-pointer flex items-center gap-3 text-black font-bold"
                  onClick={() => handleCategorySelect(cat)}
                >
                  {categoryIcons[cat]} <span>{cat}</span>
                </div>
              ))}
            </div>
          </div>

          {category && (
            <form
              className="flex flex-col gap-3 mt-5 w-[90%] sm:w-[70%] md:w-[50%] lg:w-[30%] mx-auto"
              onSubmit={(e) => {
                e.preventDefault();
                handleSearch();
              }}
            >
              <input
                className="w-full px-3 py-2.5 rounded border border-yellow-500"
                placeholder="Search phone number..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <button
                type="submit"
                className="mb-4 flex items-center justify-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded font-bold mt-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin" /> Searching...
                  </>
                ) : (
                  <>
                    <FaSearch /> Search
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="main-banner bg-cover bg-center bg-fixed">
        <main className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="w-full text-center">
            {error && (
              <div className="bg-red-100 text-red-700 p-3.5 rounded mt-2.5 font-bold text-center">
                {error}
              </div>
            )}

            <div className="results-list">
              {loading ? (
                <div className="text-white flex items-center justify-center gap-2">
                  <FaSpinner className="animate-spin" />
                  Loading data...
                </div>
              ) : result ? (
                <div className="bg-white p-6 rounded-lg border border-yellow-500 mt-5 text-black">
                  <div className="flex items-center font-bold text-xl mb-4 text-yellow-500">
                    <FaUser className="mr-2" /> Shelf Corporation Information
                  </div>

                  {/* Hide Pipeline Stage and Status In Portal fields */}
                  <div className="flex flex-wrap gap-4 mobile-width">
                    {Object.entries(result)
                      .filter(([key]) => !['pipeline stage', 'status in portal'].includes(key.toLowerCase()))
                      .map(([key, value]) => (
                        <div className="w-full sm:flex-1 sm:min-w-[45%] px-4 py-3 text-black" key={key}>
                          <label className="block font-bold mb-1 text-left">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </label>
                          <input
                            style={{ border: "1px solid #e9e7e7ff", padding: "14px" }}
                            className="w-full px-2.5 py-2 text-gray-700 bg-white rounded shadow-sm"
                            value={value || '-'}
                            readOnly
                          />
                        </div>
                      ))}
                  </div>

                  {/* Alert with heading */}
                  {result['Status In Portal'] && <AlertBox message={result['Status In Portal']} />}
                </div>
              ) : null}

              {(hasSearched || result) && (
                <div className="mt-5">
                  <button
                    onClick={handleReset}
                    className="flex items-center justify-center w-full gap-5 px-4 py-3 bg-yellow-500 text-black rounded font-bold"
                  >
                    <FaRedo /> Reset
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="mt-auto bg-gray-800 text-white text-center py-6">
        <div className="w-full max-w-[1366px] px-5 mx-auto">
          <div className="flex items-center justify-between pb-8 border-b border-yellow-500/20">
            <div className="footer-img">
              <img
                src="https://msgsndr-private.storage.googleapis.com/companyPhotos/dee76044-7b84-4f98-a487-4c1da2c2cc0e.png"
                alt="logo"
                className="mx-auto h-28 w-auto object-contain"
              />
            </div>
            <div className="flex items-center gap-5 text-2xl">
              <a href="https://www.facebook.com/businessfundingsecrets" className="hover:text-yellow-500 transition-colors">
                <i className="fa fa-facebook"></i>
              </a>
              <a href="https://www.instagram.com/derrickpwhitehead" className="hover:text-yellow-500 transition-colors">
                <i className="fa fa-instagram"></i>
              </a>
              <a href="https://www.linkedin.com/in/derrickpwhitehead/" className="hover:text-yellow-500 transition-colors">
                <i className="fa fa-linkedin"></i>
              </a>
              <a href="https://x.com/economicmasonry" className="hover:text-yellow-500 transition-colors">
                <i className="fa fa-twitter"></i>
              </a>
            </div>
          </div>
        </div>
        <p className="pt-5">&copy; 1983 - 2025 - Alpha Incorporated</p>
      </footer>
    </div>
  );
}

export default App;
